/**
 * @file DiseaseField.js
 * @description disease autocomplete 
 *              uses curated diseases as default options
 *              and queries server api for adhoc options.
 *              has check icon for diseses options from the
 *              geneModels
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 *  *  options structure:
 *  [
        {
            name: name,
            id: id,
            source: 'MONDO',
            approved: true,
        }
    ]
 *   
 *   regarding warning: 'React Hook useMemo received a function whose dependencies are unknown'
 *   https://kyleshevlin.com/debounce-and-throttle-callbacks-with-react-hooks
 *   
 * @author don michael
 * @since Dec 28, 2021 15:42
 */
import debounce from 'lodash/debounce';
import axiosClient from 'src/services/axiosClient.js';

import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Autocomplete } from '@mui/material';
import Box from '@mui/material/Box';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TextField from '@mui/material/TextField';

import { arrayExists, objectExists, objectKeyExists, stringExists } from 'src/@common/utilities';

const callApi = (searchTerm) => {

    return new Promise((resolve, reject) => {

        if (stringExists(searchTerm)) {

            const baseUrl = import.meta.env.VITE_APP_API_BASE_URL;

            const config = {
                method: 'post',
                baseURL: baseUrl,
                url: '/disease/diseaseSearch',
                data: {
                    searchTerm,
                },
            };

            axiosClient(config)
                .then((response) => {
                    resolve(response.data.payload);
                })
                .catch((error) => {
                    reject(error);
                });

        } else {
            resolve();
        }

    });
}

function DiseaseField(props) {
    const {
        fieldName = 'disease',
        fieldLabel = 'Disease',
        currentValue,
        sxStyle,
        diseaseCurations = [],
        fieldErrors = {},
        helpText = '',
        isFocus = false,
        handleBlur = () => { },
        handleChange,
    } = props;

    const [helperText, setHelperText] = useState('');
    const [noOptionsText, setNoOptionsText] = useState('Enter a disease name');
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);

    const [value, setValue] = useState({
        id: '',
        name: '',
        source: '',
    });

    const [loading, setLoading] = useState(false);

    const debouncedSearch = useMemo(
        () =>
            debounce((text) => {

                setNoOptionsText('Loading...');
                setLoading(true)
                setOptions([]);

                callApi(text)
                    .then((results) => {

                        if (arrayExists(results)) {
                            setOptions(results);
                        } else {
                            setNoOptionsText('No diseases found');
                            setOptions([]);
                        }

                        setLoading(false);

                    }).catch((err) => {
                        console.error(err);
                        setLoading(false);
                    });
            }, 700),
        []
    )

    // useEffect(() => {
    //     if (objectExists(currentValue) &&
    //         arrayExists(options)) {

    //         const match = options.find((item) => {
    //             return item.id === currentValue.id;
    //         });

    //     }
    // }, [currentValue, options]);

    useEffect(() => {
        // add any provided disease curations to the options
        if (arrayExists(diseaseCurations)) {
            const newOptions = [];

            diseaseCurations.forEach((disease) => {
                newOptions.push(disease);
            });

            // mui complains if the currentValue does not exist in
            // the options, so add it.
            if (objectExists(currentValue)) {

                const existingOption = newOptions.find((item) => {
                    return item.id === currentValue.id;
                });

                if (!existingOption) {
                    newOptions.unshift(currentValue);
                }
            }

            setOptions(newOptions);
        }

    }, [diseaseCurations, currentValue]);

    useEffect(() => {
        if (objectExists(currentValue)) {
            setValue(currentValue);
        }
    }, [currentValue]);

    useEffect(() => {

        if (fieldErrors[fieldName]) {
            setHelperText(fieldErrors[fieldName])
        } else {
            if (objectExists(value)) {

                if (stringExists(helpText)) {
                    setHelperText(helpText);

                } else {

                    if (value.source) {
                        setHelperText(`${value.source}-${value.id}`);
                    } else {
                        setHelperText(value.id)
                    }
                }

            } else {
                if (stringExists(helpText)) {
                    setHelperText(helpText);
                } else {
                    setHelperText('')
                }
            }
        }

    }, [fieldErrors, fieldName, helpText, value]);

    const onInputChange = (event, newValue, reason) => {
        // using this changeEvent to identify keystroke input,
        // indicating a needed call to the api to provide new options 

        switch (reason) {
            case 'clear':
                break;

            case 'reset':
                break;

            case 'input':

                // update local state and inform the parent of the change
                onChange(null, newValue);

                // keystroke input.  hit the api
                debouncedSearch(newValue, (apiOptions) => {

                    if (!arrayExists(apiOptions)) {
                        setOptions([])

                    } else {

                        const transformedOptions = [];

                        apiOptions.forEach((option) => {
                            transformedOptions.push({
                                name: option.name,
                                id: option.id,
                                source: option.source,
                            });
                        });

                        setOptions(apiOptions);
                    }
                });
                break;

            default:
                break;
        }
    };

    const onChange = (event, newValue) => {
        // this change event provides the newly selected object.
        // update both the local and parent state.

        // set the local state
        if (newValue) {
            setValue(newValue);
        } else {
            setValue({
                name: '',
                id: '',
                source: '',
                approved: false,
            });
        }

        // wrap in a synthetic event
        const newEvent = {
            target: {
                type: 'autocomplete',
                value: newValue,
            }
        };

        // call the parent change handler
        handleChange(newEvent, 'disease');
    };

    return (
        <Autocomplete
            id={fieldName}
            name={fieldName}
            disablePortal={true}
            disableClearable={true}
            autoSelect={false}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            getOptionLabel={(option) => {
                return typeof option === 'string' ? option : option.name
            }}
            isOptionEqualToValue={(option, value) => {
                if (objectExists(value) &&
                    stringExists(value.id)) {
                    return option.id === value.id
                } else {
                    return true;
                }
            }}
            filterOptions={(x) => x}  // must disable filtering on client
            includeInputInList
            onBlur={() => {
                handleBlur(fieldName);
            }}
            onInputChange={onInputChange}
            onChange={onChange}
            options={options}
            loading={loading}
            noOptionsText={noOptionsText}
            value={value}
            sx={sxStyle}
            renderOption={(props, option) => (
                <Box component="li" sx={{ mr: 2 }} {...props} key={option.id}>
                    {objectKeyExists(option, 'approved') && option.approved &&
                        <CheckCircleOutlineIcon sx={{ mr: 1 }} color={'success'} />
                    }
                    {option.name}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={fieldLabel}
                    size="small"
                    margin="dense"
                    variant="filled"
                    error={Boolean(fieldErrors[fieldName])}
                    autoFocus={isFocus}
                    helperText={helperText}
                    multiline={false}
                />
            )}
        />
    );
}

DiseaseField.propTypes = {
    fieldLabel: PropTypes.string,
    fieldName: PropTypes.string,
    currentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object,]),
    diseaseCurations: PropTypes.array,
    fieldErrors: PropTypes.object,
    helpText: PropTypes.string,
    isFocus: PropTypes.bool,
    handleBlur: PropTypes.func,
    handleChange: PropTypes.func.isRequired,
};

export default DiseaseField;
